import type { ComponentType } from "react";
import React, { Suspense, lazy } from "react";
import type { LoaderFunctionArgs, PathRouteProps, RouteObject } from "react-router-dom";
import { Navigate, Outlet } from "react-router-dom";

import GET_ACTOR_QUERY from "pages/Actors/ActorView/graphql/getActorQuery";
import BOOKING_QUOTATION_REFERENCE_QUERY from "pages/BookingQuotations/Detail/graphql/bookingQuotationReferenceQuery";
import GET_DRIVER_QUERY from "pages/Drivers/graphql/getDriverQuery";
import GET_OPERATION_QUERY from "pages/Operations/OperationsView/graphql/getOperation";
import GET_PRODUCT_QUERY from "pages/Products/graphql/getProductQuery";
import GET_ROUTE_QUERY from "pages/Routes/graphql/getRouteQuery";
import GET_TRAILER_QUERY from "pages/Trailers/graphql/getTrailerQuery";
import GET_VEHICLE_QUERY from "pages/Vehicles/graphql/getVehicleQuery";
import i18n from "translations/i18n";

import LoadingScreen from "../components/shared/LoadingScreen";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
import AuthenticationGuard from "./components/AuthenticationGuard";
import ExternalSessionGuard from "./components/ExternalSessionGuard";
import {
  ACTORS_PATH,
  BOOKING_QUOTATIONS_PATH,
  CONFIGURE_LEAD_TIMES_MODAL_PATH,
  COSTS_PATH,
  DASHBOARD_PATH,
  DRIVERS_PATH,
  EDIT_PATH,
  EXTERNAL_INVITE_PATH,
  EXTERNAL_PATH,
  GLOBAL_TRACKING_PATH,
  INSIGHTS_PATH,
  INTEGRATIONS_DOCS_PATH,
  INTEGRATIONS_LOGS_PATH,
  KANBAN_PATH,
  LEAD_TIMES_PATH,
  LPCOS_PATH,
  MERCANTE_PATH,
  NEW_PATH,
  NOT_AUTHORIZED_PATH,
  OPERATIONS_PATH,
  OPERATIONS_TABLE_PATH,
  ORDERS_MODAL_PATH,
  PORT_TERMINALS_PATH,
  PROCESSES_MODAL_PATH,
  PRODUCTS_PATH,
  REQUEST_PATH,
  ROOT_AGENTS,
  ROOT_PATH,
  ROUTES_PATH,
  SELECT_WORKSPACE_PATH,
  SETTINGS_CREDENTIALS_PATH,
  SETTINGS_NOTIFICATION_GROUPS_PATH,
  SETTINGS_PATH,
  SETTINGS_USERS_PATH,
  SETTINGS_WORKSPACE_PATH,
  TRAILERS_PATH,
  TRASH_PATH,
  VEHICLES_PATH,
} from "./paths";
import type { ICrumbDataParam } from "./types";
import factoryRouteComponents from "./utils/factoryRouteComponents";
import { dashboardsLoader } from "./utils/loaders/dashboardsLoader";
import { loaderCurrentUser } from "./utils/loaders/loaderCurrentUser";
import { loaderWithQueryByIdParams } from "./utils/loaders/loaderWithQueryByIdParams";

const Loadable =
  <TProps extends object = React.PropsWithChildren>(
    Component: ComponentType<TProps>,
    fallback: React.ReactNode = <LoadingScreen />,
  ): React.FC<TProps> =>
  (props: TProps) => (
    <Suspense fallback={fallback}>
      <Component {...props} />
    </Suspense>
  );

const ActorsList = Loadable(lazy(() => import("../pages/Actors/ActorsList")));
const ActorView = Loadable(lazy(() => import("../pages/Actors/ActorView")));
const AgentsExecutions = Loadable(lazy(() => import("../pages/Agents/Executions")));
const AgentsList = Loadable(lazy(() => import("../pages/Agents/List")));
const BookingQuotationDetail = Loadable(lazy(() => import("../pages/BookingQuotations/Detail")));
const BookingQuotationsList = Loadable(lazy(() => import("../pages/BookingQuotations/List")));
const Costs = Loadable(lazy(() => import("../pages/Operations/Costs")));
const Dashboard = Loadable(lazy(() => import("../pages/Dashboard")));
const DashboardFrame = Loadable(lazy(() => import("../pages/Dashboard/DashboardFrame")));
const DriverNew = Loadable(lazy(() => import("../pages/Drivers/DriverNew")));
const DriversList = Loadable(lazy(() => import("../pages/Drivers/DriversList")));
const EditDriver = Loadable(lazy(() => import("../pages/Drivers/EditDriver")));
const EditProducts = Loadable(lazy(() => import("../pages/Products/EditProduct")));
const EditRoute = Loadable(lazy(() => import("../pages/Routes/EditRoute/EditRoute.container")));
const EditTrailer = Loadable(lazy(() => import("../pages/Trailers/EditTrailer")));
const EditVehicle = Loadable(lazy(() => import("../pages/Vehicles/EditVehicle")));
const ExternalInvite = Loadable(lazy(() => import("../pages/External")));
const GlobalTracking = Loadable(lazy(() => import("../pages/GlobalTracking")));
const InsightsPage = Loadable(lazy(() => import("../pages/Insights")));
const IntegrationDocs = Loadable(lazy(() => import("pages/Integrations/Docs")));
const IntegrationLogs = Loadable(lazy(() => import("../pages/Integrations/Logs")));
const IntegrationsLayout = Loadable(lazy(() => import("../pages/Integrations/IntegrationsLayout")));
const Layout = Loadable(lazy(() => import("../layouts/Layout")));
const LeadTimes = Loadable(lazy(() => import("../pages/Operations/LeadTimes")));
const LpcosList = Loadable(lazy(() => import("../pages/Lpcos/List")));
const Mercante = Loadable(lazy(() => import("../pages/MaritimeTransport/Mercante")));
const NewActor = Loadable(lazy(() => import("../pages/Actors/NewActor")));
const NewAgent = Loadable(lazy(() => import("../pages/Agents/New")));
const NewRoute = Loadable(lazy(() => import("../pages/Routes/NewRoute")));
const NewTrailer = Loadable(lazy(() => import("../pages/Trailers/NewTrailer")));
const NewVehicle = Loadable(lazy(() => import("../pages/Vehicles/NewVehicle")));
const NotAuthorized = Loadable(lazy(() => import("../pages/NotAuthorized")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
const OperationsList = Loadable(lazy(() => import("../pages/Operations/OperationsList")));
const OperationsTable = Loadable(lazy(() => import("../pages/Operations/OperationsTable")));
const OperationsView = Loadable(lazy(() => import("../pages/Operations/OperationsView")));
const OperationTrash = Loadable(lazy(() => import("../pages/Operations/OperationTrash")));
const ProductsList = Loadable(lazy(() => import("../pages/Products/ProductsList")));
const ProductsNew = Loadable(lazy(() => import("../pages/Products/NewProduct")));
const RequestsTableOutlet = Loadable(lazy(() => import("../pages/Integrations/Logs/components/RequestsTableOutlet")));
const RoutesList = Loadable(lazy(() => import("../pages/Routes/RoutesList")));
const SelectWorkspace = Loadable(lazy(() => import("../pages/SelectWorkspace")));
const Settings = Loadable(lazy(() => import("../pages/Settings")));
const SettingsCredentials = Loadable(lazy(() => import("../pages/Settings/Credentials")));
const SettingsNotificationGroups = Loadable(lazy(() => import("../pages/Settings/NotificationGroups")));
const SettingsUsers = Loadable(lazy(() => import("../pages/Settings/Users")));
const SettingsWorkspace = Loadable(lazy(() => import("../pages/Settings/Workspace")));
const TrailersList = Loadable(lazy(() => import("../pages/Trailers/TrailersList")));
const VehiclesList = Loadable(lazy(() => import("../pages/Vehicles/VehiclesList")));
const AgentsExecutionModal = Loadable(
  lazy(() => import("../pages/Agents/Executions/components/Modal")),
  null,
);
const ConfigureLeadTimes = Loadable(
  lazy(() => import("../pages/Operations/LeadTimes/ConfigureLeadTimesModal")),
  null,
);
const LpcoDrawer = Loadable(
  lazy(() => import("../pages/Lpcos/List/components/Table/DrawerRoute")),
  null,
);
const OrderModal = Loadable(
  lazy(() => import("../components/Modals/EditForeignTradeOrderModal")),
  null,
);
const ProcessModal = Loadable(
  lazy(() => import("../components/Modals/ProcessModal")),
  null,
);
const RequestModal = Loadable(
  lazy(() => import("../pages/Integrations/Logs/components/RequestModal")),
  null,
);
const ReportDataGridWrapper = Loadable(
  lazy(() => import("../pages/Operations/OperationsTable/components/ReportDataGrid/Wrapper")),
);

const Routes: RouteObject[] = [
  {
    path: "*",
    element: <LogoOnlyLayout />,
    children: [
      { path: "404", element: <NotFound /> },
      { path: NOT_AUTHORIZED_PATH, element: <NotAuthorized /> },
      {
        path: "*",
        element: (
          <Navigate
            to="/404"
            replace
          />
        ),
      },
      { path: SELECT_WORKSPACE_PATH, element: <AuthenticationGuard component={SelectWorkspace} /> },
    ],
  },
  {
    path: ROOT_PATH,
    element: <AuthenticationGuard component={Layout} />,
    shouldRevalidate: ({ currentUrl, nextUrl }) => currentUrl.pathname !== nextUrl.pathname,
    loader: async (loaderArgs: LoaderFunctionArgs) => {
      const data = await loaderCurrentUser(loaderArgs);

      return data;
    },
    id: "root",
    children: [
      {
        path: OPERATIONS_PATH,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.operations") }) },
        children: [
          { index: true, element: <OperationsList /> },
          {
            path: OPERATIONS_TABLE_PATH,
            element: <OperationsTable />,
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("operations.tabs.table") }) },
            children: [
              { path: ":report_group_id", element: <ReportDataGridWrapper /> },
              {
                path: ":report_group_id/:report_id",
                element: <ReportDataGridWrapper />,
                children: [
                  { path: `${ORDERS_MODAL_PATH}/:order_id`, element: <OrderModal /> },
                  { path: `${PROCESSES_MODAL_PATH}/:process_id`, element: <ProcessModal /> },
                ],
              },
            ],
          },
          {
            path: LEAD_TIMES_PATH,
            element: <LeadTimes />,
            children: [{ path: CONFIGURE_LEAD_TIMES_MODAL_PATH, element: <ConfigureLeadTimes /> }],
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("operations.tabs.lead_times") }),
            },
          },
          {
            path: `${KANBAN_PATH}/${EDIT_PATH}`,
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
            id: "kanban",
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_OPERATION_QUERY, "operation", "title");

              return data;
            },
            element: <OperationsView />,
            children: [
              { path: `${ORDERS_MODAL_PATH}/:order_id`, element: <OrderModal /> },
              { path: `${PROCESSES_MODAL_PATH}/:process_id`, element: <ProcessModal /> },
            ],
          },
          {
            path: `${KANBAN_PATH}/${EDIT_PATH}/${TRASH_PATH}`,
            element: <OperationTrash />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_OPERATION_QUERY, "operation", "title");

              return data;
            },
            handle: {
              crumb: (data: ICrumbDataParam) => ({
                path: data.path,
                label: `${data.label} - ${i18n.t("operation_trash.title")}`,
              }),
            },
          },
          {
            path: COSTS_PATH,
            element: <Costs />,
            children: [],
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("operations.tabs.costs") }) },
          },
        ],
      },
      {
        path: SETTINGS_PATH,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.settings") }) },
        children: [
          { index: true, element: <Settings /> },
          { path: SETTINGS_USERS_PATH, element: <SettingsUsers /> },
          { path: SETTINGS_WORKSPACE_PATH, element: <SettingsWorkspace /> },
          { path: SETTINGS_CREDENTIALS_PATH, element: <SettingsCredentials /> },
          { path: SETTINGS_NOTIFICATION_GROUPS_PATH, element: <SettingsNotificationGroups /> },
        ],
      },
      {
        path: DASHBOARD_PATH,
        id: "dashboard",
        loader: async () => {
          const data = await dashboardsLoader();

          return data;
        },
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: "Dashboard" }) },
        element: <Dashboard />,
        children: [{ path: ":dashboard_id", element: <DashboardFrame /> }],
      },
      {
        path: DRIVERS_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("drivers.drivers_list.page_title") }),
        },
        children: [
          { index: true, element: <DriversList /> },
          {
            path: EDIT_PATH,
            element: <EditDriver />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_DRIVER_QUERY, "driver", "name");

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
          {
            path: NEW_PATH,
            element: <DriverNew />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("drivers.drivers_new.page_title") }),
            },
          },
        ],
      },
      {
        path: VEHICLES_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("vehicles.vehicles_list.page_title") }),
        },
        children: [
          { index: true, element: <VehiclesList /> },
          {
            path: NEW_PATH,
            element: <NewVehicle />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("vehicles.new_vehicle.page_title") }),
            },
          },
          {
            path: EDIT_PATH,
            element: <EditVehicle />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_VEHICLE_QUERY, "vehicle", "plate");

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
        ],
      },
      {
        path: TRAILERS_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("trailers.trailers_list.page_title") }),
        },
        children: [
          { index: true, element: <TrailersList /> },
          {
            path: NEW_PATH,
            element: <NewTrailer />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("trailers.new_trailer.page_title") }),
            },
          },
          {
            path: EDIT_PATH,
            element: <EditTrailer />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_TRAILER_QUERY, "trailer", "plate");

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
        ],
      },
      {
        path: PRODUCTS_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("products.products_list.page_title") }),
        },
        children: [
          { index: true, element: <ProductsList /> },
          {
            path: NEW_PATH,
            element: <ProductsNew />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({
                path: data.path,
                label: i18n.t("products.products_new.page_title"),
              }),
            },
          },
          {
            path: EDIT_PATH,
            element: <EditProducts />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(
                loaderArgs,
                GET_PRODUCT_QUERY,
                "product",
                entityData => `[${entityData.code}] ${entityData.description}`,
              );

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
        ],
      },
      {
        path: ACTORS_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("actors.actors_list.page_title") }),
        },
        children: [
          { index: true, element: <ActorsList /> },
          {
            path: NEW_PATH,
            element: <NewActor />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("actors.new_actor.page_title") }),
            },
          },
          {
            path: EDIT_PATH,
            element: <ActorView />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_ACTOR_QUERY, "actor", "nickname");

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
        ],
      },
      {
        path: ROUTES_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("routes.routes_list.page_title") }),
        },
        children: [
          { index: true, element: <RoutesList /> },
          {
            path: NEW_PATH,
            element: <NewRoute />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("routes.new_route.page_title") }),
            },
          },
          {
            path: EDIT_PATH,
            element: <EditRoute />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(loaderArgs, GET_ROUTE_QUERY, "route", "name");

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
        ],
      },
      {
        path: `${MERCANTE_PATH}/:port?`,
        element: <Mercante />,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.mercante") }) },
      },
      {
        path: PORT_TERMINALS_PATH,
        // eslint-disable-next-line jsx-a11y/heading-has-content
        element: <h1 />,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.port_terminals") }) },
      },
      {
        path: INSIGHTS_PATH,
        element: <InsightsPage />,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.insights") }) },
      },
      {
        element: <IntegrationsLayout />,
        children: [
          {
            path: INTEGRATIONS_DOCS_PATH,
            element: <IntegrationDocs />,
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.docs") }) },
          },
          {
            path: INTEGRATIONS_LOGS_PATH,
            element: <IntegrationLogs />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.integration_logs") }),
            },
            children: [
              {
                path: ":operation_id",
                element: <RequestsTableOutlet />,
                children: [{ path: `${REQUEST_PATH}/:request_id`, element: <RequestModal /> }],
              },
            ],
          },
        ],
      },
      {
        path: GLOBAL_TRACKING_PATH,
        element: <GlobalTracking />,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.tracking") }) },
        children: [
          { index: true, element: <></> },
          { path: `${PROCESSES_MODAL_PATH}/:process_id`, element: <ProcessModal /> },
        ],
      },
      {
        path: ROOT_AGENTS,
        id: "agents",
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("agents.list.page_title") }) },
        children: [
          { index: true, element: <AgentsList /> },
          {
            path: "executions",
            element: <AgentsExecutions />,
            handle: {
              crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("agents.executions.page_title") }),
            },
            children: [{ path: ":execution_id", element: <AgentsExecutionModal /> }],
          },
          {
            path: "new",
            element: <NewAgent />,
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.new_agent") }) },
          },
        ],
      },
      {
        path: LPCOS_PATH,
        element: <LpcosList />,
        handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.lpcos") }) },
        children: [{ path: ":lpco_id", element: <LpcoDrawer /> }],
      },
      {
        path: BOOKING_QUOTATIONS_PATH,
        handle: {
          crumb: (data: ICrumbDataParam) => ({ path: data.path, label: i18n.t("sidebar.booking_quotations") }),
        },
        children: [
          { index: true, element: <BookingQuotationsList /> },
          {
            path: EDIT_PATH,
            element: <BookingQuotationDetail />,
            loader: async (loaderArgs: LoaderFunctionArgs) => {
              const data = await loaderWithQueryByIdParams(
                loaderArgs,
                BOOKING_QUOTATION_REFERENCE_QUERY,
                "bookingQuotation",
                bookingQuotation => bookingQuotation?.process?.referenceCode,
              );

              return data;
            },
            handle: { crumb: (data: ICrumbDataParam) => ({ path: data.path, label: data.label }) },
          },
        ],
      },
    ],
  },
];

const ExternalRoutes: RouteObject[] = [
  {
    path: EXTERNAL_PATH,
    element: <Outlet />,
    children: [{ path: EXTERNAL_INVITE_PATH, element: <ExternalSessionGuard component={ExternalInvite} /> }],
  },
];

// TODO: Solve type error in the function to don't need of assertion)
export const externalRouteElements = factoryRouteComponents(ExternalRoutes as PathRouteProps[]);
export const internalRouteElements = factoryRouteComponents(Routes as PathRouteProps[]);
